import React, { useState, useMemo } from "react";
import { Button, Chip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import ConvertQuotationModal from "./ConvertQuotationModal";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import {
  AG_GRID_REACT_HEADER_ROW_HEIGHT,
  ERP_LABEL_CLOSE,
  ERP_LABEL_PRINT_QUOTATION,
  ERP_LABEL_PREVIEW_QUOTATION,
} from "../../../core/constants/erpConstants";
import { useOrganization } from "../../../configurations/OrganizationContext";
import HeaderFooterComponent from "../../printConvertion/HeaderAndFooter";
import { generateQuotationHtml } from "./quotationPrintUtils";

export default function ViewERPQuotation({
  quotationData,
  onBackToList,
  serverResposnes,
  selectedLocationDetails,
}) {
  const org = useOrganization();
  const masterLocationDetails = useSelector((state) => state.erpDetailsState.masterLocationDetails);
  const [convertOpen, setConvertOpen] = useState(false);
  const patientName = quotationData?.customerName || "";
  const [printdata, setPrintData] = useState("");
  const [triggerAction, setTriggerAction] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePrintQuotation = async (actionType = "print") => {
    try {
      setLoading(true);
      const htmlData = await generateQuotationHtml(quotationData, {
        patientName,
        patientInfo: quotationData?.patientInfo || null,
        org,
        masterLocationDetails,
      });
      setPrintData(htmlData);
      setTriggerAction(actionType);
    } catch (err) {
      console.error("Error printing quotation:", err);
      if (typeof serverResposnes === "function") {
        serverResposnes(
          "Failed to generate quotation print: " + (err?.message || err),
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = useMemo(() => ({ width: "100%", height: "300px" }), []);
  const gridStyle = useMemo(() => ({ alignItems: "start", height: "100%", width: "100%" }), []);
  
  const autoSizeStrategy = useMemo(() => ({ type: "fitGridWidth" }), []);
  const defaultColDef = useMemo(() => ({ sortable: true, flex: 1, filter: true, resizable: true, floatingFilter: true }), []);

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(d);
    }
  };

  const fmtAmount = (v) => v != null ? parseFloat(v).toFixed(2) : "0.00";

  const lines = useMemo(() => quotationData?.lines || [], [quotationData?.lines]);

  const grossSubtotal = useMemo(() => {
    return lines.reduce((acc, l) => {
      const grossUnit = l.mrp != null ? l.mrp : (l.priceSell != null ? l.priceSell : (l.price != null ? l.price : 0));
      return acc + (grossUnit * (l.units || 1));
    }, 0);
  }, [lines]);

  const totalDiscountAll = useMemo(() => {
    const linesDisc = lines.reduce((acc, l) => {
      const grossUnit = l.mrp != null ? l.mrp : (l.priceSell != null ? l.priceSell : (l.price != null ? l.price : 0));
      const netUnit = l.price != null ? l.price : grossUnit;
      return acc + (Math.max(0, grossUnit - netUnit) * (l.units || 1));
    }, 0);
    if (linesDisc > 0) return linesDisc;
    return parseFloat(quotationData?.discountRs) || 0;
  }, [lines, quotationData?.discountRs]);

  const payableAmt = quotationData?.totalAmount != null ? parseFloat(quotationData.totalAmount) : Math.max(0, grossSubtotal - totalDiscountAll);

  if (!quotationData) {
    return (
      <div className="p-3">
        <Button startIcon={<ArrowBack />} onClick={onBackToList} size="small">
          Back to List
        </Button>
        <p className="text-muted mt-3">No quotation selected.</p>
      </div>
    );
  }

  // Mapped to quotation data
  const viewInvoiceColumnDefs = [
    {
      headerName: "SL No",
      field: "slNo",
      colId: "slNo",
      tooltipField: "SL No",
      valueGetter: "node.rowIndex + 1",
    },
    {
      headerName: "Service Name",
      field: "productName",
      colId: "productName",
      valueGetter: "data.name || data.product || '—'",
      tooltipField: "Product Name",
    },
    {
      headerName: "Original Charges/per unit",
      field: "originalPriceSell",
      colId: "originalPrice",
      cellStyle: { textAlign: "right" },
      valueGetter: "data.mrp != null ? data.mrp : (data.priceSell != null ? data.priceSell : (data.price != null ? data.price : 0))",
      valueFormatter: (params) => params.value != null ? parseFloat(params.value).toFixed(2) : '0.00',
      tooltipField: "Original Price Inclusive of Tax",
    },
    {
      headerName: "Discount Charges/per unit",
      field: "discount",
      colId: "discount",
      cellStyle: { textAlign: "right" },
      valueGetter: (params) => {
        const d = params.data;
        if (!d) return 0;
        const grossUnit = d.mrp != null ? d.mrp : (d.priceSell != null ? d.priceSell : (d.price != null ? d.price : 0));
        const netUnit = d.price != null ? d.price : grossUnit;
        return Math.max(0, grossUnit - netUnit);
      },
      valueFormatter: (params) => params.value != null ? parseFloat(params.value).toFixed(2) : '0.00',
      tooltipField: "Discount Charges Inc of Tax",
    },
    {
      headerName: "Tax Rate",
      field: "taxRate",
      colId: "taxRate",
      valueGetter: "data.taxRate || 0",
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) => params.value != null ? parseFloat(params.value).toFixed(2) : '0.00',
      tooltipField: "TaxRate of the product",
    },
    {
      headerName: "Units",
      field: "units",
      colId: "units",
      cellStyle: { textAlign: "right" },
      valueGetter: "data.units || 1",
      tooltipField: "Quantity",
    },
    {
      headerName: "Price",
      field: "price",
      colId: "price",
      valueGetter: (params) => {
        const d = params.data;
        if (!d) return 0;
        const netUnit = d.price != null ? d.price : ((d.priceSell || 0) - (d.discount || 0));
        return netUnit * (d.units || 1);
      },
      valueFormatter: (params) => params.value != null ? parseFloat(params.value).toFixed(2) : '0.00',
      cellStyle: { textAlign: "right" },
      tooltipField: "Total Price",
    },
  ];

  return (
    <div style={{ padding: "12px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "8px" }}>
        <Button startIcon={<ArrowBack />} onClick={onBackToList} size="small" variant="outlined" color="inherit">
          Back to List
        </Button>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Chip label={"Quotation # " + (quotationData.billNumber || quotationData.quoteId || "—")} color="primary" variant="outlined" size="small" />
          {Boolean(patientName) && (
            <Chip
              icon={<PersonIcon fontSize="small" />}
              label={patientName}
              variant="outlined"
              size="small"
              sx={{ borderColor: "#cbd5e1", color: "#334155", fontWeight: 500 }}
            />
          )}
          <Button variant="contained" size="small" onClick={() => setConvertOpen(true)} sx={{ backgroundColor: "#4f46e5", "&:hover": { backgroundColor: "#4338ca" } }}>
            Convert to Invoice
          </Button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-start invoiceTitle ms-2 mt-2">
        <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "100px" }}>
          <h6 className="mb-1">Quotation ID</h6>
          <span className="text-muted">{quotationData.billNumber || quotationData.quoteId || "—"}</span>
        </div>
        {Boolean(patientName) && (
          <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "140px" }}>
            <h6 className="mb-1">Patient Name</h6>
            <span className="text-muted">{patientName}</span>
          </div>
        )}
        <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "100px" }}>
          <h6 className="mb-1">Date</h6>
          <span className="text-muted">{fmtDate(quotationData.dateNew || quotationData.timestamp)}</span>
        </div>
        {/* <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "160px" }}>
          <h6 className="mb-1">Total Bill Amount</h6>
          <span className="text-muted">{fmtAmount(grossSubtotal > 0 ? grossSubtotal : quotationData.totalAmount)}</span>
        </div> */}
        <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "160px" }}>
          <h6 className="mb-1">Total Discount</h6>
          <span className="text-muted">{fmtAmount(totalDiscountAll)}</span>
        </div>
        {/* <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "120px" }}>
          <h6 className="mb-1">Round Off</h6>
          <span className="text-muted">{quotationData.roundOff || "0.00"}</span>
        </div> */}
        <div className="me-4 mb-2 flex-fill flex-lg-grow-0" style={{ minWidth: "160px" }}>
          <h6 className="mb-1">
            {/* Payable Amount */}
            Total Bill Amount
            </h6>
          <span className="text-muted">{fmtAmount(payableAmt)}</span>
        </div>
      </div>

      <div className="ms-1" style={containerStyle}>
        <div className="ag-theme-balham ag-style ag-header-left px-1 py-2 w-100" style={gridStyle}>
          <AgGridReact
            autoSizeStrategy={autoSizeStrategy}
            rowData={lines}
            defaultColDef={defaultColDef}
            columnDefs={viewInvoiceColumnDefs}
            headerHeight={AG_GRID_REACT_HEADER_ROW_HEIGHT}
            suppressHeaderFocus={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end d-grid gap-3 mt-3 px-2">
        <Button
          className="p-2 my-2 bg-primary text-white"
          variant="contained"
          onClick={() => handlePrintQuotation("preview")}
          disabled={loading}
          sx={{
            textTransform: "none",
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          {ERP_LABEL_PREVIEW_QUOTATION}
        </Button>
        <Button
          className="p-2 my-2 bg-primary text-white"
          variant="contained"
          onClick={() => handlePrintQuotation("print")}
          disabled={loading}
          sx={{
            textTransform: "none",
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          {ERP_LABEL_PRINT_QUOTATION}
        </Button>
        <Button
          className="p-2 my-2 bg-danger text-white"
          variant="contained"
          onClick={onBackToList}
          disabled={loading}
          sx={{
            textTransform: "none",
            backgroundColor: "#d32f2f",
            "&:hover": { backgroundColor: "#c62828" },
          }}
        >
          {ERP_LABEL_CLOSE}
        </Button>
      </div>

      {printdata && triggerAction && (
        <HeaderFooterComponent
          title="Quotation"
          data={printdata}
          triggerAction={triggerAction}
          onComplete={() => {
            setPrintData("");
            setTriggerAction("");
            console.log("Quotation print action completed and state cleared.");
          }}
        />
      )}

      <ConvertQuotationModal
        open={convertOpen}
        quotationData={quotationData}
        onClose={() => setConvertOpen(false)}
        onSuccess={() => {
          setConvertOpen(false);
          if (typeof onBackToList === "function") onBackToList();
        }}
        serverResposnes={serverResposnes}
        selectedLocationDetails={selectedLocationDetails}
        masterLocationDetails={masterLocationDetails}
      />
    </div>
  );
}
